import { useEffect, useMemo, useRef, useState } from 'react';
import MapComponent, { MapRef, Marker, NavigationControl } from 'react-map-gl';
import { bbox, center, featureCollection, point } from '@turf/turf';
import RoomIcon from '@material-ui/icons/Room';
import { Box, Button, ButtonGroup, makeStyles, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { GalleryPhoto, GalleryPhotoLocationData } from '../types/galleryTypes';

type LocatedPhoto = GalleryPhoto & { locationData: GalleryPhotoLocationData };

const MARKER_COLOR = '#007cbf';
const SELECTED_MARKER_COLOR = '#FB8C00';

const MAP_STYLES = [
  { label: 'Map', value: 'mapbox://styles/mapbox/streets-v11' },
  { label: 'Satellite', value: 'mapbox://styles/mapbox/satellite-streets-v12' },
];

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: 16,
    overflow: 'hidden',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gridGap: '0.5rem',
    height: '100%',
    width: '100%',
    opacity: 0.4,
  },
  styleToggle: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 2,
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
  },
  toggleButton: {
    textTransform: 'none',
    padding: '2px 12px',
    fontSize: '13px',
  },
  toggleButtonActive: {
    backgroundColor: '#007cbf !important',
    color: '#FFFFFF !important',
  },
}));

interface GalleryPhotosMapProps {
  photos: GalleryPhoto[];
  selectedPhotoUuid?: string;
  onPhotoClick?: (photo: GalleryPhoto) => void;
  showStyleToggle?: boolean;
}

export const GalleryPhotosMap = observer(
  ({
    photos,
    selectedPhotoUuid,
    onPhotoClick,
    showStyleToggle = true,
  }: GalleryPhotosMapProps) => {
    const classes = useStyles();
    const { environmentStore } = useStore();
    const token = environmentStore.environmentVariables.mapBoxToken;

    const mapRef = useRef<MapRef>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapStyle, setMapStyle] = useState(MAP_STYLES[0].value);

    const locatedPhotos = useMemo(
      () =>
        (photos || []).filter(
          (photo): photo is LocatedPhoto =>
            typeof photo?.locationData?.latitude === 'number' &&
            typeof photo?.locationData?.longitude === 'number'
        ),
      [photos]
    );

    const points = useMemo(() => {
      if (locatedPhotos.length === 0) return null;
      return featureCollection(
        locatedPhotos.map(photo =>
          point([photo.locationData.longitude, photo.locationData.latitude])
        )
      );
    }, [locatedPhotos]);

    const [longitude, latitude] = useMemo(() => {
      if (!points) return [0, 0];
      return center(points).geometry.coordinates;
    }, [points]);

    useEffect(() => {
      if (mapLoaded && mapRef?.current && points) {
        const bounds = bbox(points);
        mapRef.current.fitBounds(
          [
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]],
          ],
          { padding: 60, maxZoom: 17, duration: 800 }
        );
      }
    }, [mapLoaded, points]);

    if (!points) {
      return (
        <Box className={classes.emptyState}>
          <RoomIcon style={{ fontSize: 48 }} />
          <Typography variant="body1">No location data available</Typography>
        </Box>
      );
    }

    return (
      <Box className={classes.root}>
        {showStyleToggle && (
          <ButtonGroup className={classes.styleToggle} size="small">
            {MAP_STYLES.map(style => (
              <Button
                key={style.value}
                className={`${classes.toggleButton} ${
                  mapStyle === style.value ? classes.toggleButtonActive : ''
                }`}
                onClick={() => setMapStyle(style.value)}
              >
                {style.label}
              </Button>
            ))}
          </ButtonGroup>
        )}
        <MapComponent
          ref={mapRef}
          initialViewState={{ longitude, latitude, zoom: 16 }}
          mapStyle={mapStyle}
          mapboxAccessToken={token}
          onLoad={() => setMapLoaded(true)}
          style={{ width: '100%', height: '100%' }}
        >
          <NavigationControl position="top-right" />
          {locatedPhotos.map(photo => {
            const isSelected = photo.uuid === selectedPhotoUuid;
            return (
              <Marker
                key={photo.uuid}
                longitude={photo.locationData.longitude}
                latitude={photo.locationData.latitude}
                onClick={
                  onPhotoClick ? () => onPhotoClick(photo) : undefined
                }
              >
                <RoomIcon
                  style={{
                    fontSize: isSelected ? 40 : 30,
                    color: isSelected ? SELECTED_MARKER_COLOR : MARKER_COLOR,
                    cursor: onPhotoClick ? 'pointer' : 'default',
                  }}
                />
              </Marker>
            );
          })}
        </MapComponent>
      </Box>
    );
  }
);
